import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleHistory } from '@fortawesome/pro-solid-svg-icons/faRectangleHistory';
import {
	ContentLoader,
	FlexAlign,
	FlexGap,
	FlexJustify,
	InfiniteLoadTrigger,
	ItemRowHeader,
	StackedAddonPageHeader,
	StackVertical,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ItemRowHeaderCell } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/Cells/ItemRowHeaderCell';
import React, { useEffect, useState } from 'react';
import { GetItemCounts, SendsHistoryEntryType } from '@ukpostbox/client-data';
import { EndpointError } from '../../../../_Global/Components/EndpointError';
import SendsHistoryItem from '../SendsHistoryItem';
import SendsHistoryFilterControl, { SendHistoryFilterButton } from './SendsHistoryFilterControl';
import { useSendMailHistory } from '../../../Providers/SendMailHistoryProvider';

export type SendsHistoryControlType = {
	hideHeader?: boolean;
	forceHeader?: boolean;
	linkToSendsItem?: boolean;
	containerRef?: any;
};

function SendsHistoryControl(props: SendsHistoryControlType) {
	const { t } = useTranslation();
	const { isTiny, isSmall, isMedium } = useLayout();
	const {
		pageId,
		userId,
		status,
		data,
		error,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		isStale,
		isRefetching,
	} = useSendMailHistory();

	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data));
		}
	}, [data]);

	return (
		<div
			className={`${props.hideHeader ? 'm-n-md rounded' : ''}`}
			style={{ overflow: props.hideHeader ? 'hidden' : 'auto' }}
		>
			{!props.hideHeader && ((!isTiny && !isSmall && !isMedium) || props.forceHeader) && (
				<StackedAddonPageHeader
					title={t('sends.profilesHistory.title')}
					postTitleAddon={<SendHistoryFilterButton />}
					extraClass='section-page-header'
				>
					<SendsHistoryFilterControl />
				</StackedAddonPageHeader>
			)}

			{!isTiny && !isSmall && (
				<ItemRowHeader title='Sends History' sticky sidePadding extraClass='sends-history-row'>
					<ItemRowHeaderCell>{t('sends.profilesHistory.headings.file')}</ItemRowHeaderCell>
					{!pageId && <ItemRowHeaderCell>{t('sends.profilesHistory.headings.pageId')}</ItemRowHeaderCell>}
					{!userId && <ItemRowHeaderCell>{t('sends.profilesHistory.headings.user')}</ItemRowHeaderCell>}
					<ItemRowHeaderCell>{t('sends.profilesHistory.headings.address')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.profilesHistory.headings.postage')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.profilesHistory.headings.date')}</ItemRowHeaderCell>
				</ItemRowHeader>
			)}

			{status === 'pending' ? (
				<ContentLoader childHeight='7em' />
			) : status === 'error' ? (
				<EndpointError {...error} stacked extraClass='m-md' />
			) : (
				<>
					{data &&
						data.pages.map((group: any) =>
							group.data.map((entry: SendsHistoryEntryType) => (
								<SendsHistoryItem
									key={`sendsHistory${entry.id}`}
									{...entry}
									linkToSend={props.linkToSendsItem}
									hidePageDetails={!!pageId}
									hideUserDetails={!!userId}
								/>
							)),
						)}

					{!data && (
						<StackVertical
							gap={FlexGap.ExtraSmall}
							align={FlexAlign.Center}
							justify={FlexJustify.Center}
							extraClass='m-xxl p-xxl text-center'
						>
							<FontAwesomeIcon icon={faRectangleHistory} size='3x' />
							<h6 className='sub-heading mt-sm mb-xs'>{t('sends.profilesHistory.noData.title')}</h6>
							<p>{t('sends.profilesHistory.noData.description')}</p>
						</StackVertical>
					)}

					{itemCounts.total === 0 && (
						<StackVertical
							gap={FlexGap.ExtraSmall}
							align={FlexAlign.Center}
							justify={FlexJustify.Center}
							extraClass='m-xxl p-xxl text-center'
						>
							<FontAwesomeIcon icon={faRectangleHistory} size='3x' />
							<h6 className='sub-heading mt-sm mb-xs'>{t('sends.profilesHistory.noResults.title')}</h6>
						</StackVertical>
					)}

					{itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}
		</div>
	);
}

export default SendsHistoryControl;
